import { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import { useParams } from "react-router-dom";
import { API, Auth } from "aws-amplify";
import "./Comparison.css";
import { saveAs } from "file-saver";
import {
  FaColumns,
  FaPlay,
  FaPlus,
  FaRegFilePdf,
  FaSpinner,
  FaTrashAlt,
} from "react-icons/fa";
import { assoc, ascend, descend, dissoc, isNil, pipe, prop, sortWith } from "ramda";
//import { fetchAuthSession } from 'aws-amplify/auth';
import { SignatureV4 }      from '@aws-sdk/signature-v4';
import { HttpRequest }      from '@aws-sdk/protocol-http';
import { Sha256 }           from '@aws-crypto/sha256-js';
import AddSamplesModal from "./AddSamplesModal";
import RemovableColGrid from "./RemovableColGrid";
import EditableText from "./EditableText";
import MultiLangButtonGroup from "./MultiLangButtonGroup";
import config from "./config";

const FN_URL = config.lambdaFn.RUN_COMPARISON_FN_URL;
const REGION = config.lambdaFn.REGION;

/**
 * A client error; the request was erroneous.
 */
class ClientError extends Error {
  constructor(message) {
    super(message);
    this.name = 'ClientError';
  }
}

const base64ToBlob = (base64Data, contentType) => {
  const byteCharacters = atob(base64Data);
  const bytes = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    bytes[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(bytes);
  return new Blob([byteArray], { type: contentType });
};

/**
 * Try to determine whether we should re-try Lambda Function URL call again.
 *
 * @param {number} status
 * @param {string} text
 * @returns {boolean}
 */
const isRetryableLambdaUrlFailure = (text) =>
  /ResourceNotReadyException|SnapStartNotReadyException|Lambda is initializing your function|function is inactive/i.test(text)

/**
 * Wait for a set amount of milliseconds.
 *
 * @param {number} ms milliseconds to wait
 */
const sleep = (ms) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const runComparison = async (id, type, params ) => {
  const credentials = await Auth.currentUserCredentials(); //await currentUserCredentials does not help
  const url  = new URL(FN_URL); // was: /comparisons/${id}/run
  const body = {
    ...params,
    "uid": credentials.identityId,
    "id": id,
    "type": type,
  };
  const req  = new HttpRequest({
    method : 'POST',
    hostname: url.hostname,
    path    : url.pathname,
    headers : {
      host        : url.hostname,
      'content-type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  const signer  = new SignatureV4({
    service: 'lambda',
    region: REGION,
    credentials: async () => credentials,
    sha256: Sha256
  });
  const { headers, body: signedBody, method } = await signer.sign(req);
  let res = await fetch(url.toString(), { method, headers, body: signedBody });
  let errorText;
  if (!res.ok) {
    errorText = await res.text();
    if (isRetryableLambdaUrlFailure(errorText)) {
      console.log("Retrying Lambda Function URL after a while");
      // retry once after sleeping
      await sleep(5000);
      res = await fetch(url.toString(), { method, headers, body: signedBody });
      if (!res.ok) {
        errorText = await res.text();
      }
    }
  }
  if (!res.ok) {
    let msg = errorText; // already set
    try {
      msg = JSON.parse(errorText).errorMessage;
    } catch {
      // ignore
    }
    if (res.status >= 400 && res.status < 500) {
      throw new ClientError(msg);
    } else {
      throw new Error(msg);
    }
  }
  const data = await res.json();
  return data;
};

const getComparison = async (id) =>
  await API.get("mgp", `/comparisons/${id}`);

const putComparison = async (id, comparison) =>
  await API.put("mgp", `/comparisons/${id}`, {
    body: comparison
  });


const FIXED_FIELDS = ['Sample_ID', 'Sample', 'Group'];

const indexOfField = ({ field }) => {
  const idx = FIXED_FIELDS.indexOf(field);
  return idx >= 0 ? idx : FIXED_FIELDS.length;
};

const sortColDefs = sortWith([
  ascend(indexOfField),
  ascend(prop("field"))
]);

const makeColDef = (field) => ({
  field,
  editable: field !== "Sample_ID"
});

const makeColDefs = (metadata) =>
  sortColDefs(Array.from(new Set(metadata.flatMap(Object.keys))).map(makeColDef));

const MAX_COMPARISON_NAME_LEN = 50;

const PDF_LANGUAGE = {
  "en": "English",
  "de": "Deutsch",
  "uk": "Українська",
};

const Comparison = () => {
  const { id } = useParams();
  const [comparison, setComparison] = useState({});
  const [isPdfLoading, setPdfLoading] = useState(false);
  const [isRunning, setRunning] = useState(false);
  const [barImg, setBarImg] = useState(null);
  const [heatImg, setHeatImg] = useState(null);
  const [pcoaImg, setPcoaImg] = useState(null);
  const [timelineImg, setTimelineImg] = useState(null);
  const [selected, setSelected] = useState([]);
  const [colDefs, setColDefs] = useState([]);
  const [showAddSamples, setShowAddSamples] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pdfLang, setPdfLang] = useState("en");
  const fetchComparison = async (id) => {
    const newComparison = await getComparison(id)
    const newColDefs = makeColDefs(newComparison.metadata);
    setComparison(newComparison);
    setColDefs(newColDefs);
    setDirty(true);
  };
  const saveComparison = async (id) => {
    await putComparison(id, comparison);
  };
  const handleAddColumn = () => {
    if (!colDefs) {
      return;
    }
    let field;
    while (!field) {
      const answer = prompt("Enter the name for the new column")?.trim();
      if (answer === null || answer === undefined) {
        return;
      }
      if (!colDefs.some((col) => col.field.toLowerCase() === answer.toLowerCase())) {
        field = answer;
      } else {
        alert("A column with the same name already exists");
      }
    }
    setColDefs((old) => old.concat({
      field: field,
      editable: true
    }));
    setDirty(true);
  };
  const handleRemoveColumn = async (field) => {
    setColDefs((old) => old.filter((col) => col.field !== field));
    const newComparison = assoc("metadata", comparison.metadata.map(dissoc(field)), comparison);
    setComparison(newComparison);
    putComparison(id, newComparison);
    setDirty(true);
  };
  const handleSortColumn = async (field, descending) => {
    const direction = descending ? descend : ascend;
    const sortMissingLast = sortWith([
      ascend(pipe(prop(field), isNil)),
      direction(prop(field))
    ]);
    const newComparison = assoc("metadata", sortMissingLast(comparison.metadata), comparison);
    setComparison(newComparison);
    putComparison(id, newComparison);
    setDirty(true);
  };
  const handleAddSamples = () => {
    setShowAddSamples(true);
  };
  const handleAddSamplesCancel = () => {
    setShowAddSamples(false);
  };
  const handleAddSamplesOk = async (sampleNames) => {
    const existingSampleNames = new Set(comparison.metadata.map(prop("Sample_ID")));
    const sampleNamesToAdd = sampleNames.filter((sampleName) => !existingSampleNames.has(sampleName));
    const newMetadata = comparison.metadata.concat(
      sampleNamesToAdd.map((sampleName) => ({
        Sample_ID: sampleName,
        Sample: sampleName,
        Group: sampleName,
      }))
    );
    const newComparison = assoc("metadata", newMetadata, comparison);
    setComparison(newComparison);
    await putComparison(id, newComparison);
    setShowAddSamples(false);
    setDirty(true);
  };
  const clearPlots = () => {
    setBarImg(null);
    setHeatImg(null);
    setPcoaImg(null);
    setTimelineImg(null);
  };
  const setPlots = ({ png_bar, png_heat, png_pcoa, png_timeline }) => {
    setBarImg(png_bar);
    setHeatImg(png_heat);
    setPcoaImg(png_pcoa);
    setTimelineImg(png_timeline);
  };
  const run = async (event) => {
    event.preventDefault();
    setRunning(true);
    clearPlots();
    // save comparison before running to be sure
    await saveComparison(id);
    try {
      const data = await runComparison(id, "png");
      setPlots(data);
      setDirty(false);
    } catch (err) {
      if (err instanceof ClientError) {
        alert(err.message);
      } else {
        alert("Error: " + err.message);
        throw err;
      }
    } finally {
      setRunning(false);
    }
  };
  const runPdf = async (event) => {
    try {
      event.preventDefault();
      setPdfLoading(true);
      if (dirty) {
        clearPlots();
        // save comparison before running to be sure
        await saveComparison(id);
      }
      const data = await runComparison(id, "pdf", { lang: pdfLang });
      // set png images
      if (dirty) {
        setPlots(data);
        setDirty(false);
      }
      // give the PDF file
      const blob = base64ToBlob(data.pdf, "application/pdf");
      saveAs(blob, `${comparison.name}.pdf`);
    } catch (err) {
      if (err instanceof ClientError) {
        alert(err.message);
      } else {
        alert("Error: " + err.message);
        throw err;
      }
    } finally {
      setPdfLoading(false);
    }
  };
  const persistName = async (name) => {
    const newComparison = assoc("name", name, comparison);
    setComparison(newComparison);
    await putComparison(id, newComparison);
  };
  const handleGridCellValueChanged = async () => {
    await saveComparison(id);
    setDirty(true);
  };
  const handleSelectionChanged = (event) => {
    const selectedNodes = event.selectedNodes;
    const selectedIds = selectedNodes.map((node) => node.data["Sample_ID"]);
    setSelected(selectedIds);
  };
  const handleDeleteSelected = async () => {
    const newMetadata = comparison.metadata.filter((item) => !selected.includes(item["Sample_ID"]));
    const newComparison = assoc("metadata", newMetadata, comparison);
    setComparison(newComparison);
    await putComparison(id, newComparison);
    setDirty(true);
  };
  const inputsOk = () => comparison?.metadata?.length > 1;
  const isBusy = () => isRunning || isPdfLoading;
  useEffect(() => {
    (async () => {
      // fetch data
      try {
        await Promise.all([fetchComparison(id)]);
      } catch (error) {
        console.log(error);
        alert(error);
      }
    })();
  }, [id]);
  return (
    <>
      <AddSamplesModal
        show={showAddSamples}
        onClickOk={handleAddSamplesOk}
        onClickCancel={handleAddSamplesCancel}
        included={comparison?.metadata?.map((obj) => obj["Sample_ID"])}
      />
      <div className="float-right">
        <ButtonToolbar>
          <Button variant="secondary"
            onClick={handleDeleteSelected}
            disabled={!(selected?.length > 0) || isBusy()}
            title={isBusy() ? "Cannot remove samples while the comparison is running" :  `Remove ${selected?.length || 0} selected sample${(selected?.length || 0) !== 1 ? "s" : ""}`}
            style={{
              marginRight: 5,
              display: (selected?.length > 0) ? "inline-block" : "none"
            }}
          >
            <FaTrashAlt /> Remove selected
          </Button>
          {" "}
          <Button variant="secondary"
            onClick={handleAddSamples}
            disabled={isBusy()}
            title={isBusy() ? "Cannot add samples while the comparison is running" : "Add more samples to the comparison"}
            style={{
              marginRight: 5
            }}
          >
            <FaPlus /> Add samples
          </Button>
          {" "}
          <Button variant="secondary"
            onClick={handleAddColumn}
            disabled={isBusy()}
            title={isBusy() ? "Cannot add a column while the comparison is running" : "Add a column"}
          >
            <FaColumns /> Add column
          </Button>
        </ButtonToolbar>
      </div>
      <h3>
        <EditableText
          id="name"
          value={comparison?.name}
          minLength={1}
          maxLength={MAX_COMPARISON_NAME_LEN}
          onValueChanged={persistName}
          readOnly={isBusy()}
        />
      </h3>
      <div style={{ marginTop: 10, marginBottom: 10 }}>
        <RemovableColGrid
            className="comparison-metadata-grid"
            domLayout="autoHeight"
            rowData={comparison.metadata}
            columnDefs={colDefs}
            defaultColDef={{
              editable: true,
              sortable: false,
              flex: 1,
            }}
            stopEditingWhenCellsLoseFocus={true}
            onCellValueChanged={handleGridCellValueChanged}
            rowSelection={{
              mode: "multiRow",
              enableClickSelection: false,
            }}
            onSelectionChanged={handleSelectionChanged}
            getRowId={(params) => params.data["Sample_ID"]}
            onRemoveColumn={handleRemoveColumn}
            onSortColumn={handleSortColumn}
            fixedFields={FIXED_FIELDS}
            readOnly={isBusy()}
        />
      </div>
      <ButtonToolbar>
        <Button
          onClick={run}
          disabled={!inputsOk() || !dirty || isBusy()}
          variant={(!inputsOk() || !dirty) ? "secondary" : "primary"}
          style={{ marginRight: 5 }}
          title={isBusy() ? "Please wait" : !inputsOk() ? "Must have at least 2 samples": "Run comparison and show plots"}
        >
          {isRunning ? <FaSpinner className="icon-spin" /> : <FaPlay />}{" "}Run comparison
        </Button>
        {" "}
        <MultiLangButtonGroup
          languages={PDF_LANGUAGE} selectedLanguage={pdfLang} onSelectLanguage={setPdfLang}
          disabled={!inputsOk() || isBusy()}
          variant={!inputsOk() ? "secondary" : "primary"}
          >
          <Button
            onClick={runPdf}
            disabled={!inputsOk() || isBusy()}
            variant={!inputsOk() ? "secondary" : "primary"}
            title={isBusy() ? "Please wait" : !inputsOk() ? "Must have at least 2 samples": "Run comparison and generate PDF"}
          >
            {isPdfLoading ? <FaSpinner className="icon-spin" /> : <FaRegFilePdf />}{" "}Generate PDF
          </Button>
        </MultiLangButtonGroup>
      </ButtonToolbar>
      <section className="beta-diversity-report">
        {barImg && (
          <section>
            <h4>Per sample genus-level relative abundance</h4>
            <figure>
              <img alt="Bar plot" width="600" src={`data:image/png;base64,${barImg}`} />
              <figcaption><strong>Relative abundance profiles across samples at the genus level.</strong> Stacked bar plots show the proportional composition of bacterial genera for each sample. Colours represent individual genera of at least 1% relative abundance.</figcaption>
            </figure>
          </section>)}
        {heatImg && (
          <section>
            <h4>Comparison of microbiome composition at genus level across samples</h4>
            <figure>
              <img alt="Heatmap" width="600" src={`data:image/png;base64,${heatImg}`} />
              <figcaption><strong>Sample comparison of genus-level abundances.</strong> Heatmap visualisation of all bacterial genera of at least 1% relative abundance depicts differences in composition across all samples. Heatmap enables direct comparison of relative abundances and presence/absence of genera between samples.</figcaption>
            </figure>
          </section>)}
        {pcoaImg && (
          <section>
            <h4>Differences in microbiome structure at genus level between samples</h4>
            <figure>
              <img alt="PCoA plot" width="600" src={`data:image/png;base64,${pcoaImg}`} />
              <figcaption><strong>Differences in gut microbiome composition at genus level between samples.</strong> Principal Coordinates Analysis (PCoA) based on Bray-Curtis dissimilarity. The plot illustrates differences in microbial community structure between samples. Each point represents a sample and each symbol a different group. Samples clustered together tend to have more similar compositions than distant samples. </figcaption>
            </figure>
          </section>)}
        {timelineImg && (
          <section>
            <figure>
              <h4>Distribution of bacterial genus across samples</h4>
              <img alt="Sequential bar plot" width="600" src={`data:image/png;base64,${timelineImg}`} />
              <figcaption><strong>Representation of genus-level composition across samples.</strong> Alluvial plot depicts the distribution and continuity of bacterial genera across samples. Inter-bar flows link identical genera to highlight shared and shifting community contributions.</figcaption>
            </figure>
          </section>)}
      </section>
    </>
  );
};

export default Comparison;
